import { useTranslation } from "@/features/utils/i18n";
import { formatFileSize } from "@/features/utils/numbers";
import { useCallback, useEffect, useRef, useState } from "react";
import { EmptyState } from "./empty-state";
import { FileItem } from "./file-item";
import { FileList } from "./file-list";
import { getFileKey } from "./helpers";
import type { FileUploadProps } from "./types";

export const FileUpload = ({
  accept,
  multiple = false,
  maxFiles = Infinity,
  type = "any",
  value,
  onChange,
  maxSize = 10 * 1024 * 1024, // 10MB default
  className,
  disabled = false,
}: FileUploadProps) => {
  const { t } = useTranslation("fileUpload");
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Convert value to files array
  useEffect(() => {
    if (!value) {
      setFiles([]);
      return;
    }

    if (value instanceof File) {
      setFiles([value]);
    } else if (Array.isArray(value)) {
      setFiles(value);
    } else if (typeof value === "string") {
      // For existing URLs, we don't have File objects
      setFiles([]);
    }
  }, [value]);

  const handleFiles = useCallback(
    (newFiles: FileList | null) => {
      if (!newFiles || newFiles.length === 0) return;

      const filesArray = Array.from(newFiles);

      // Validate file sizes
      const oversized = filesArray.filter((f) => f.size > maxSize);
      if (oversized.length > 0) {
        alert(t("errors.tooLarge", { size: formatFileSize(maxSize) }));
        return;
      }

      if (multiple) {
        // Check maxFiles limit
        const remainingSlots = maxFiles - files.length;
        if (remainingSlots <= 0) {
          alert(t("errors.tooMany", { count: maxFiles }));
          return;
        }

        // Take only as many files as we have slots for
        const filesToAdd = filesArray.slice(0, remainingSlots);
        if (filesToAdd.length < filesArray.length) {
          alert(
            t("errors.partlyAdded", {
              added: filesToAdd.length,
              count: maxFiles,
            }),
          );
        }

        // Add to existing files
        const updatedFiles = [...files, ...filesToAdd];
        setFiles(updatedFiles);
        onChange?.(updatedFiles);
      } else {
        setFiles([filesArray[0]]);
        onChange?.(filesArray[0]);
      }
    },
    [multiple, maxSize, maxFiles, onChange, files, t],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (!disabled) handleFiles(e.dataTransfer.files);
    },
    [disabled, handleFiles],
  );

  const handleClick = useCallback(() => {
    if (!disabled) fileInputRef.current?.click();
  }, [disabled]);

  const handleRemove = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index);
      setFiles(newFiles);
      onChange?.(multiple ? newFiles : null);
    },
    [files, multiple, onChange],
  );

  // Hidden file input
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={accept}
      multiple={multiple}
      className="hidden"
      onChange={(e) => handleFiles(e.target.files)}
      disabled={disabled}
    />
  );

  // Empty state
  if (files.length === 0 && !(typeof value === "string" && value)) {
    return (
      <>
        {fileInput}
        <EmptyState
          type={type}
          maxSize={maxSize}
          isDragging={isDragging}
          disabled={disabled}
          className={className}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        />
      </>
    );
  }

  // Multiple mode - always show FileList (even with 1 file) to display "Add more" button
  if (multiple && files.length > 0) {
    return (
      <>
        {fileInput}
        <FileList
          files={files}
          disabled={disabled}
          className={className}
          onRemove={handleRemove}
          onAddMore={handleClick}
          maxFiles={maxFiles}
        />
      </>
    );
  }

  // Single file display (non-multiple mode)
  const file = files[0];
  if (!file) return null;

  const fileKey = getFileKey(file);

  return (
    <>
      {fileInput}
      <FileItem
        file={file}
        fileKey={fileKey}
        disabled={disabled}
        className={className}
        onRemove={() => handleRemove(0)}
      />
    </>
  );
};
